import { Box, Button, Divider, Drawer, Step, StepLabel, Stepper, Typography } from '@mui/material';
import CloseButton from 'components/@home/drawers/CloseButton';
import { useConfirm } from 'components/common/GlobalConfirmDialog';
import StepDataMapping from 'components/@home/@documents/SentDocuments/edit-steps/StepDataMapping';
import {
  buildSubmitPayload,
  getEditWizardSteps,
  validateStep,
} from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import SetupDocumentMeta from 'components/@home/@documents/SentDocuments/edit-steps/SetupDocumentMeta';
import StepReview from 'components/@home/@documents/SentDocuments/edit-steps/StepReview';
import StepSetupDocument from 'components/@home/@documents/SentDocuments/edit-steps/StepSetupDocument';
import useTemplateEditor from 'components/@home/@documents/SentDocuments/useTemplateEditor';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { HR_BOT_APP_ID } from 'services/constants';
import apps from 'store/app/entities/apps/action';
import {
  useCreateDocumentFormatMutation,
  useDeleteDocumentFormatMutation,
  useEditDocumentFormatMutation,
} from 'store/app/entities/SharedFilesSlice';
import { I18n } from 'utils/i18n';

export const EditDrawerContext = React.createContext({});

const EditDrawer = () => {
  const dispatch = useDispatch();
  const { file: format, isOpen, close } = useContext(EditDrawerContext);

  const [editDocumentFormat] = useEditDocumentFormatMutation();
  const [createDocumentFormat] = useCreateDocumentFormatMutation();
  const [deleteDocumentFormat] = useDeleteDocumentFormatMutation();
  const { open: openConfirm, close: closeConfirm } = useConfirm();

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editorSession, setEditorSession] = useState(0);
  const prevIsOpenRef = useRef(false);

  const editorResetKey = useMemo(
    () =>
      `${editorSession}_${
        format?._id || format?.templateAttachment || format?.layoutSampleAttachment || 'new'
      }`,
    [editorSession, format?._id, format?.templateAttachment, format?.layoutSampleAttachment],
  );

  const {
    template,
    setTemplate,
    fields,
    syncFieldsToTemplate,
    canvasPdfSrc,
    uploadBusy,
    onPdfDrop,
    onReplacePdf,
    currentSignerIndex,
    setCurrentSignerIndex,
    placeholderMembers,
  } = useTemplateEditor({
    format,
    defaultMessage: I18n.t('HrBot.defaultMessage'),
    resetKey: editorResetKey,
  });

  const actions = {
    apps: bindActionCreators(apps, dispatch),
  };
  const app = useSelector(state => state.apps.apps?.[HR_BOT_APP_ID]);

  useEffect(() => {
    if (!loading && app?._id && !app?.groups) {
      setLoading(true);
      actions.apps.getGroups(app._id).then(() => setLoading(false));
    }
  }, [actions.apps, app?._id, app?.groups, loading]);

  useEffect(() => {
    setActiveStep(0);
  }, [format]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!wasOpen && isOpen) {
      setActiveStep(0);
    }
    if (wasOpen && !isOpen) {
      setEditorSession(s => s + 1);
    }
  }, [isOpen]);

  const wizardTemplate = useMemo(() => ({ ...template, fields }), [template, fields]);
  const stepKeys = useMemo(() => getEditWizardSteps(wizardTemplate), [wizardTemplate]);
  const stepKey = stepKeys[activeStep];
  const isLastStep = activeStep === stepKeys.length - 1;
  const canUseXml = !!app?.settings?.hrbotAllowXmlTemplates;
  const canContinue = validateStep(stepKey, wizardTemplate);

  const stepLabel = key => I18n.t(`HrBot.EditTemplate.Wizard.steps.${key}`);

  const handleSubmit = async () => {
    const form = buildSubmitPayload({ ...template, fields });
    if (format) {
      await editDocumentFormat({ id: format?._id, body: form });
    } else {
      await createDocumentFormat(form);
    }
    close();
  };

  const handleDeleteTemplate = () => {
    if (!format?._id) return;
    openConfirm({
      isDelete: true,
      message: I18n.t('HrBot.EditTemplate.deleteTemplateConfirm', { name: format.name }),
      onConfirm: async () => {
        try {
          await deleteDocumentFormat(format._id).unwrap();
        } finally {
          closeConfirm();
          close();
        }
      },
    });
  };

  const renderStep = () => {
    switch (stepKey) {
      case 'setupDocument':
        return (
          <StepSetupDocument
            template={template}
            setTemplate={setTemplate}
            fields={fields}
            syncFieldsToTemplate={syncFieldsToTemplate}
            canvasPdfSrc={canvasPdfSrc}
            currentSignerIndex={currentSignerIndex}
            setCurrentSignerIndex={setCurrentSignerIndex}
            placeholderMembers={placeholderMembers}
            uploadBusy={uploadBusy}
            onPdfDrop={onPdfDrop}
            onReplacePdf={onReplacePdf}
            canUseXml={canUseXml}
          />
        );
      case 'dataMapping':
        return <StepDataMapping template={template} setTemplate={setTemplate} />;
      case 'review':
      default:
        return (
          <StepReview
            template={template}
            setTemplate={setTemplate}
            uploadBusy={uploadBusy}
            onPdfDrop={onPdfDrop}
            canvasPdfSrc={canvasPdfSrc}
            onReplacePdf={onReplacePdf}
            submitEnabled={canContinue && isLastStep}
            onEnterSubmit={handleSubmit}
          />
        );
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={close} onClick={e => e.stopPropagation()}>
      <Box
        sx={{
          width: { xs: '100%', md: '95vw', xl: '90vw' },
          maxWidth: 'calc(100vw - 16px)',
          height: '100vh',
          display: 'flex',
          flexDirection: 'row',
          bgcolor: 'background.paper',
        }}
      >
        {/* Same pattern as EmployeeDrawer: close column on the inner-left */}
        <Box sx={{ flexShrink: 0, pt: 2.5, pl: 2.5 }}>
          <CloseButton onClick={close} />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              pt: 2,
              pb: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" component="h1" sx={{ fontWeight: 700, pr: 1 }}>
                {I18n.t(`HrBot.tabs.${format ? 'editTemplate' : 'newTemplate'}.title`, {
                  appName: app?.name,
                  name: format?.name,
                })}
              </Typography>
            </Box>
            <Stepper activeStep={activeStep} alternativeLabel={stepKeys.length >= 4}>
              {stepKeys.map(k => (
                <Step key={k}>
                  <StepLabel>{stepLabel(k)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', px: 3, py: 2 }}>{renderStep()}</Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', md: 'block' } }}
            />
            <Box
              sx={{
                width: { xs: '100%', md: 320 },
                flexShrink: 0,
                overflow: 'auto',
                px: { xs: 3, md: 0 },
                py: 2,
                pr: { md: 2 },
                borderTop: { xs: 1, md: 0 },
                borderColor: 'divider',
              }}
            >
              <SetupDocumentMeta
                template={template}
                setTemplate={setTemplate}
                fields={fields}
                canvasPdfSrc={stepKey === 'setupDocument' ? canvasPdfSrc : null}
                uploadBusy={uploadBusy}
                onReplacePdf={onReplacePdf}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {format?._id && (
                <Button variant="outlined" color="error" onClick={handleDeleteTemplate}>
                  {I18n.t('HrBot.EditTemplate.deleteTemplate')}
                </Button>
              )}
              {activeStep > 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setActiveStep(s => s - 1)}
                >
                  {I18n.t('Back')}
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button variant="outlined" color="secondary" onClick={close}>
                {I18n.t('HrBot.Cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!canContinue}
                onClick={isLastStep ? handleSubmit : () => setActiveStep(s => s + 1)}
              >
                {isLastStep ? I18n.t('HrBot.Save') : I18n.t('Next')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EditDrawer;
